import {
  FileFolderItem,
  MissionDetailsSelection,
  RunDetailsSelection,
  useDataContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { ProjectDataPanel } from '@trimble/project-data-panel-ui';
import {
  PROJECT_DATA_FILTER_OPTIONS,
  ProjectDataFilterMode,
} from '../project-data.types';
import { Alert, BtnClasses, Button, Spinner } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { useSearchParams } from '@solidjs/router';
import { JSX, Show, batch, createMemo, createSignal } from 'solid-js';
import { SharedProjectDataTrees } from './SharedProjectDataTrees';

export interface SharedProjectDataPanelProps {
  onModelDetailsSelect?(model: FileFolderItem): void;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
}

const WorkspaceDataEditingActions = (): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const [saveError, setSaveError] = createSignal(false);
  const [isSaving, setIsSaving] = createSignal(false);

  const handleDone = async (): Promise<void> => {
    batch(() => {
      setSaveError(false);
      setIsSaving(true);
    });
    try {
      const saved = await fxWorkspace.commitWorkspaceDataEditing();
      if (!saved) {
        setSaveError(true);
      }
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    fxWorkspace.cancelWorkspaceDataEditing();
  };

  return (
    <div
      class="sticky bottom-0 z-10 mt-auto border-t border-base-200 bg-base-page"
      data-testid="workspace-edit-actions"
    >
      <Show when={saveError()}>
        <Alert type="error">
          {t(
            fxWorkspace.isEditingWorkspaceReferencesLoaded()
              ? 'mobileMapping.fxWorkspace.workspaceDataEditing.saveFailed'
              : 'mobileMapping.fxWorkspace.workspaceDataEditing.notReady'
          )}
        </Alert>
      </Show>
      <div class="flex justify-end gap-2 p-3">
        <Button
          color={BtnClasses.SECONDARY}
          type="hollow"
          data-cy="workspace-edit-cancel"
          disabled={isSaving()}
          onClick={handleCancel}
        >
          {t('mobileMapping.fxWorkspace.workspaceDataEditing.cancel')}
        </Button>
        <Button
          color={BtnClasses.PRIMARY}
          data-cy="workspace-edit-done"
          disabled={
            isSaving() || !fxWorkspace.isEditingWorkspaceReferencesLoaded()
          }
          onClick={() => {
            void handleDone();
          }}
        >
          {t('mobileMapping.fxWorkspace.workspaceDataEditing.done')}
        </Button>
      </div>
    </div>
  );
};

export const SharedProjectDataPanel = (
  props: SharedProjectDataPanelProps
): JSX.Element => {
  const { dataState } = useDataContext();
  const fxWorkspace = useFxWorkspaceContext();
  const [searchParams, setSearchParams] = useSearchParams<{
    pointCloudIds?: string;
    trajectoryIds?: string;
    modelIds?: string;
    filterMode?: string;
  }>();

  const hasDataInUrl =
    !!searchParams.pointCloudIds ||
    !!searchParams.trajectoryIds ||
    !!searchParams.modelIds;

  const initialFilterMode: ProjectDataFilterMode =
    searchParams.filterMode === 'all' || searchParams.filterMode === 'active'
      ? searchParams.filterMode
      : hasDataInUrl
        ? 'active'
        : 'all';

  const isAnyLoading = createMemo(() => {
    const isNotReady = (state: LoadState): boolean =>
      state === LoadState.Loading || state === LoadState.NotLoaded;

    return (
      isNotReady(dataState.trajectory.state) ||
      isNotReady(dataState.pointCloud.state) ||
      isNotReady(dataState.tcFileFolder.state) ||
      isNotReady(dataState.mission.state)
    );
  });

  const filterOptions = PROJECT_DATA_FILTER_OPTIONS().filter(
    (option) => option.key !== 'archived'
  );

  return (
    <ProjectDataPanel
      initialFilterMode={initialFilterMode}
      onFilterModeChange={(mode) => setSearchParams({ filterMode: mode })}
      filterOptions={filterOptions}
    >
      <div class="flex min-h-full flex-col">
        <div class="min-h-0 flex-1">
          <Show
            when={!isAnyLoading()}
            fallback={
              <div class="h-[300px]">
                <Spinner show={true} />
              </div>
            }
          >
            <SharedProjectDataTrees
              onModelDetailsSelect={props.onModelDetailsSelect}
              onRunDetailsSelect={props.onRunDetailsSelect}
              onMissionDetailsSelect={props.onMissionDetailsSelect}
            />
          </Show>
        </div>
        <Show when={fxWorkspace.isWorkspaceDataEditing()}>
          <WorkspaceDataEditingActions />
        </Show>
      </div>
    </ProjectDataPanel>
  );
};
